import { API_PREFIX, paginatedFromDto, type HttpClient } from "@nasam/kernel";

import type { GprsCommandsRepository } from "../domain";
import {
  gprsCatalogCommandFromDto,
  gprsCatalogResponseDtoSchema,
  gprsCommandFromDto,
  gprsCommandResponseDtoSchema,
  gprsCommandsListResponseDtoSchema,
  sendGprsCommandResponseDtoSchema,
} from "./gprsDtos";

const GPRS_COMMANDS_PATH = `${API_PREFIX}/devices/gprs-commands`;

/** The catalog repeats a list parameter (`?deviceIds=a&deviceIds=b`), which
 *  isn't how the HTTP client writes arrays (`deviceIds[]=`). */
function repeated(lists: Readonly<Record<string, readonly string[] | undefined>>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [name, values] of Object.entries(lists)) {
    for (const value of values ?? []) params.append(name, value);
  }
  return params;
}

/** GPRS commands (`/devices/gprs-commands`). */
export function createGprsCommandsHttpRepository(http: HttpClient): GprsCommandsRepository {
  return {
    async list(query) {
      const response = await http.get(GPRS_COMMANDS_PATH, { params: query });
      return paginatedFromDto(
        gprsCommandsListResponseDtoSchema.parse(response),
        gprsCommandFromDto,
      );
    },
    async get(id) {
      const response = await http.get(`${GPRS_COMMANDS_PATH}/${encodeURIComponent(id)}`);
      return gprsCommandFromDto(gprsCommandResponseDtoSchema.parse(response).data);
    },
    async catalog(query) {
      const response = await http.get(`${GPRS_COMMANDS_PATH}/catalog`, {
        params: repeated({ deviceIds: query?.deviceIds, accountIds: query?.accountIds }),
      });
      return gprsCatalogResponseDtoSchema.parse(response).data.map(gprsCatalogCommandFromDto);
    },
    async send(input) {
      const parsed = sendGprsCommandResponseDtoSchema.parse(
        await http.post(GPRS_COMMANDS_PATH, input),
      );
      return { ...parsed.data, message: parsed.message ?? null };
    },
  };
}
